import * as Hooks from 'preact/hooks';

// Use latest to always have a valid response if the request has been used at least once (potentially stale data)
// use result to ensure the response is the most up-to-date response which will be undefined while the request is in-flight.
export type AsyncResult<U, E = unknown> = {
    result?: U;
    latest?: U;
    error?: E;
    retry: () => void;
};

/**
 * Essentially an asynchronous useMemo.
 * Use where you want to memoise an asynchronously computed/fetched result. This prevents
 * older results replacing later results incorrectly, and follows the same reevaluation
 * semantics as useMemo.
 *
 * Note: although stale results are suppressed, `useAsync` is unable to actually cancel
 * in-flight promises when inputs change. Using this for asynchronous side effects is
 * therefore hazardous and not recommended. This should only be used for asynchronous
 * computation and fetching, not manipulating state or other side effects.
 */

export function useAsync<T>(f: () => Promise<T>, inputs?: unknown[]): AsyncResult<T> {
    const [result, setResult] = Hooks.useState<T | undefined>(undefined);
    const [latest, setLatest] = Hooks.useState<T | undefined>(undefined);
    const [error, setError] = Hooks.useState<unknown | undefined>(undefined);
    const [retryWatch, setRetryWatch] = Hooks.useState<{}>({});

    Hooks.useEffect(() => {
        let superseded = false;

        f().then(
            result => {
                if (!superseded) {
                    setResult(result);
                    setLatest(result);
                }
            },
            error => {
                if (!superseded) setError(error);
            },
        );

        return () => {
            superseded = true;
            setResult(undefined);
            setError(undefined);
        };
    }, [retryWatch, ...(inputs ?? [])]);

    return {
        result,
        latest,
        error,
        retry: () => {
            setError(undefined);
            setRetryWatch({});
        },
    };
}

/**
 * A version of `useAsync` which dispatches a global error action with the error
 * payload when the promise rejects. In reality this is rarely what we really
 * want to do - call sites should really be handling the errors they expect to
 * see. This has been extracted from the main `useAsync` hook as that's a general
 * utility hook that should remain oblivious to both specific application behaviour
 * and any particular error handling strategy.
 */
export function useAsyncThrowError<T>(
    f: () => Promise<T>,
    inputs?: unknown[],
): AsyncResult<T, never> {
    const { result, latest, error, retry } = useAsync(f, inputs);

    if (error !== undefined) {
        throw error;
    }

    return { result, latest, retry };
}

export function useAsyncValues<T>(valuePromises: Array<Promise<T>>): Array<T> {
    const [values, setValues] = Hooks.useState<Array<T>>([]);

    Hooks.useEffect(() => {
        let superseded = false;
        valuePromises.forEach(p =>
            p.then(value => {
                if (!superseded) {
                    setValues(prev => [...prev, value]);
                }
            }),
        );
        return () => {
            superseded = true;
            setValues([]);
        };
    }, [valuePromises]);

    return values;
}
